import { useEffect, useState } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X, Plus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCreateAccountingContact, useUpdateAccountingContact } from '@/hooks';
import type { AccountingContact } from '@/lib/schemas';

const formSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  title: z.string().optional(),
  emails: z
    .array(z.object({ value: z.string().email('Invalid email address') }))
    .min(1, 'At least one email is required'),
});

type FormData = z.infer<typeof formSchema>;

interface AccountingContactModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  establishmentId?: number;
  /** When provided, modal is in edit mode */
  contact?: AccountingContact;
  /**
   * Fired after a successful create/update. Used by InvoiceDetailPage to
   * trigger a re-snapshot of the invoice's recipient contacts so the Bill To
   * pills refresh in the same render.
   */
  onSaveSuccess?: () => void;
  /**
   * Human-readable label of what this contact is being scoped to — an
   * establishment name when establishmentId is set, otherwise the org name.
   * Surfaces in the modal copy so the user can tell whether they're adding
   * a per-location contact or a fallback that applies across the whole client.
   */
  scopeLabel?: string;
}

export function AccountingContactModal({
  open,
  onOpenChange,
  organizationId,
  establishmentId,
  contact,
  onSaveSuccess,
  scopeLabel,
}: AccountingContactModalProps) {
  const isEdit = !!contact;

  const { control, handleSubmit, formState: { errors }, reset } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      title: '',
      emails: [{ value: '' }],
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'emails' });

  const createContact = useCreateAccountingContact();
  const updateContact = useUpdateAccountingContact();

  // Reset form when contact changes or modal opens
  useEffect(() => {
    if (open) {
      if (contact) {
        reset({
          name: contact.name,
          title: contact.title ?? '',
          // v0.14.7 (#176, duality-class guard): `emails` can be null on a row
          // flipped from general → accounting in place (scalar-vs-array
          // duality). `null.length` / `null.map` would crash the edit modal.
          // The backend now normalizes emails[] on read, but guard here too so
          // a stale cache / pre-fix row never bricks the form.
          emails: (contact.emails?.length ?? 0) > 0
            ? contact.emails!.map((e) => ({ value: e }))
            : [{ value: '' }],
        });
      } else {
        reset({ name: '', title: '', emails: [{ value: '' }] });
      }
      setSaveError(null);
    }
  }, [open, contact, reset]);

  // R1 AC17: when the save fails the dialog stays open AND says why, in the dialog (the toast alone is gone
  // in a few seconds, and a dialog that just sits there reads as "nothing happened, click again").
  const [saveError, setSaveError] = useState<string | null>(null);

  const onSubmit = async (data: FormData) => {
    const emails = data.emails.map((e) => e.value);
    const title = data.title?.trim() || null;
    setSaveError(null);

    try {
      if (isEdit) {
        await updateContact.mutateAsync({
          organizationId,
          contactId: contact.id,
          establishmentId,
          name: data.name,
          title,
          emails,
        });
      } else {
        await createContact.mutateAsync({
          organizationId,
          establishmentId,
          name: data.name,
          title,
          emails,
        });
      }
      onSaveSuccess?.();
      onOpenChange(false);
    } catch (error) {
      // The hook shows the toast; the dialog keeps the reason so it is still on screen after the toast fades.
      setSaveError(error instanceof Error && error.message ? error.message : 'The contact could not be saved. Please try again.');
    }
  };

  const handleCancel = () => {
    reset();
    onOpenChange(false);
  };

  const isPending = createContact.isPending || updateContact.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? 'Edit Accounting Contact' : 'Add Accounting Contact'}
            {scopeLabel && (
              <span className="text-sm font-normal text-muted-foreground"> for {scopeLabel}</span>
            )}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Update the billing contact details.'
              : establishmentId
                ? `This contact will receive invoices for ${scopeLabel ?? 'this location'} only.`
                : `This contact will receive invoices for ${scopeLabel ?? 'this client'} when no location-specific contact is set.`}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
          {/* Name */}
          <div>
            <Label htmlFor="ac-name">Name *</Label>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <Input id="ac-name" placeholder="e.g., Sarah Lee" {...field} />
              )}
            />
            {errors.name && (
              <p className="text-sm text-red-600 mt-1">{errors.name.message}</p>
            )}
          </div>

          {/* Title */}
          <div>
            <Label htmlFor="ac-title">
              Title <span className="text-gray-500 text-sm">(Optional)</span>
            </Label>
            <Controller
              name="title"
              control={control}
              render={({ field }) => (
                <Input id="ac-title" placeholder="e.g., Accounts Payable" {...field} />
              )}
            />
          </div>

          {/* Email addresses */}
          <div>
            <Label>Email Addresses *</Label>
            <div className="space-y-2 mt-1">
              {fields.map((field, index) => (
                <div key={field.id} className="flex gap-2">
                  <Controller
                    name={`emails.${index}.value`}
                    control={control}
                    render={({ field: inputField }) => (
                      <Input
                        type="email"
                        placeholder="billing@company.com"
                        {...inputField}
                        className="flex-1"
                      />
                    )}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="shrink-0 text-gray-400 hover:text-red-500"
                    onClick={() => remove(index)}
                    disabled={fields.length === 1}
                    aria-label="Remove email"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
            {errors.emails && !Array.isArray(errors.emails) && (
              <p className="text-sm text-red-600 mt-1">{errors.emails.message}</p>
            )}
            {Array.isArray(errors.emails) &&
              errors.emails.map((err, i) =>
                err?.value ? (
                  <p key={i} className="text-sm text-red-600 mt-1">
                    Email {i + 1}: {err.value.message}
                  </p>
                ) : null
              )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={() => append({ value: '' })}
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Email
            </Button>
          </div>

          {saveError && (
            <p className="text-sm text-red-700" role="alert" data-testid="accounting-contact-save-error">
              {saveError}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? 'Saving...' : isEdit ? 'Save Changes' : 'Add Contact'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
